import React, { useEffect, useState, useCallback } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { api, formatCurrency } from "@/lib/api";
import { LineChart, Line, ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import { format, parseISO } from "date-fns";
import { logger } from "@/lib/logger";
import { toast } from "sonner";
import { History, TrendingUp, TrendingDown, Trash2, Pencil, Check, X } from "lucide-react";

// Hoisted stable style objects (avoid new refs on every render)
const AXIS_TICK = { fontSize: 11, fill: "#5F7368" };
const TOOLTIP_CONTENT_STYLE = { background: "#fff", border: "1px solid #EBEBEA", borderRadius: 12 };
const LINE_DOT = { r: 4, fill: "#1C7C54" };

export default function ValuationHistoryDialog({ open, onOpenChange, investment, onChanged }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deletingId, setDeletingId] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [editValue, setEditValue] = useState("");
  const [savingEdit, setSavingEdit] = useState(false);

  const load = useCallback(() => {
    if (!investment) return;
    setLoading(true);
    api.get(`/investments/${investment.id}/valuations`)
      .then((r) => setRows(r.data))
      .catch((e) => logger.error("valuations load", e))
      .finally(() => setLoading(false));
  }, [investment]);

  useEffect(() => {
    if (!open || !investment) return;
    load();
  }, [open, investment, load]);

  // Reset any in-progress edit when the dialog closes so it doesn't leak state.
  useEffect(() => {
    if (!open) {
      setEditingId(null);
      setEditValue("");
    }
  }, [open]);

  const handleDelete = async (row) => {
    setDeletingId(row.id);
    try {
      await api.delete(`/investments/${investment.id}/valuations/${row.id}`);
      toast.success("Tasación eliminada");
      // Refresh local table + tell the parent to refetch investments list so
      // the "Valor actual" and P/L update to the previous valuation.
      load();
      onChanged?.();
    } catch (e) {
      logger.error("valuation delete", e);
      toast.error("No se pudo eliminar la tasación");
    } finally {
      setDeletingId(null);
    }
  };

  const startEdit = (row) => {
    setEditingId(row.id);
    setEditValue(String(row.value));
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditValue("");
  };

  const saveEdit = async (row) => {
    const num = Number(editValue);
    if (!editValue.trim() || Number.isNaN(num) || num < 0) {
      toast.error("Introduce un importe válido");
      return;
    }
    if (num === Number(row.value)) {
      cancelEdit();
      return;
    }
    setSavingEdit(true);
    try {
      await api.patch(`/investments/${investment.id}/valuations/${row.id}`, { value: num });
      toast.success("Tasación actualizada");
      cancelEdit();
      load();
      onChanged?.();
    } catch (e) {
      logger.error("valuation edit", e);
      toast.error("No se pudo guardar el cambio");
    } finally {
      setSavingEdit(false);
    }
  };

  if (!investment) return null;
  const currency = investment.currency || "USD";
  const initial = rows[0]?.value ?? investment.purchase_price;
  const latest = rows[rows.length - 1]?.value ?? investment.current_price ?? investment.purchase_price;
  const delta = latest - initial;
  const deltaPct = initial > 0 ? (delta / initial * 100) : 0;
  const up = delta >= 0;

  const chartData = rows.map((r) => ({
    date: format(parseISO(r.date), "dd MMM yy"),
    value: r.value,
  }));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl bg-white max-h-[92vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl flex items-center gap-2">
            <History className="w-5 h-5 text-[#1C7C54]" />
            Historial de tasaciones — {investment.name}
          </DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="p-6 text-[#5F7368]">Cargando…</div>
        ) : rows.length === 0 ? (
          <div className="p-8 text-sm text-[#5F7368]">
            Aún no hay tasaciones. Actualiza el "Valor actual" del inmueble para guardar snapshots.
          </div>
        ) : (
          <div className="flex flex-col gap-4">
            <div className="grid grid-cols-3 gap-3">
              <div className="rounded-xl border border-[#EBEBEA] px-3 py-2">
                <div className="text-[10px] uppercase tracking-[0.1em] font-bold text-[#8A9992]">Inicial</div>
                <div className="font-display text-lg font-semibold tabular-nums">{formatCurrency(initial, currency)}</div>
              </div>
              <div className="rounded-xl border border-[#EBEBEA] px-3 py-2">
                <div className="text-[10px] uppercase tracking-[0.1em] font-bold text-[#8A9992]">Actual</div>
                <div className="font-display text-lg font-semibold tabular-nums">{formatCurrency(latest, currency)}</div>
              </div>
              <div className="rounded-xl border border-[#EBEBEA] px-3 py-2">
                <div className="text-[10px] uppercase tracking-[0.1em] font-bold text-[#8A9992]">Variación</div>
                <div className={`font-display text-lg font-semibold tabular-nums flex items-center gap-1 ${up ? "text-[#1C7C54]" : "text-[#D95D39]"}`}>
                  {up ? <TrendingUp className="w-4 h-4" /> : <TrendingDown className="w-4 h-4" />}
                  {up ? "+" : ""}{deltaPct.toFixed(2)}%
                </div>
              </div>
            </div>

            {chartData.length >= 2 && (
              <div className="h-56 w-full min-w-0">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#EBEBEA" vertical={false} />
                    <XAxis dataKey="date" tick={AXIS_TICK} axisLine={false} tickLine={false} />
                    <YAxis
                      tick={AXIS_TICK}
                      axisLine={false} tickLine={false} width={70}
                      tickFormatter={(v) => formatCurrency(v, currency).replace(/\.00$/, "")}
                    />
                    <Tooltip
                      formatter={(v) => formatCurrency(v, currency)}
                      contentStyle={TOOLTIP_CONTENT_STYLE}
                    />
                    <Line type="monotone" dataKey="value" stroke="#1C7C54" strokeWidth={2} dot={LINE_DOT} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            )}

            <div className="border border-[#EBEBEA] rounded-xl overflow-auto max-h-56">
              <table className="w-full text-sm">
                <thead className="bg-[#FAFAF9] sticky top-0 z-10">
                  <tr className="text-xs uppercase tracking-[0.08em] text-[#8A9992]">
                    <th className="text-left px-4 py-2">Fecha</th>
                    <th className="text-right px-4 py-2">Valor</th>
                    <th className="w-10 px-2 py-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {[...rows].reverse().map((r) => {
                    const isEditing = editingId === r.id;
                    return (
                      <tr key={r.id} className="border-t border-[#EBEBEA]" data-testid={`valuation-row-${r.id}`}>
                        <td className="px-4 py-2 text-[#5F7368] tabular-nums">
                          {format(parseISO(r.date), "dd MMM yyyy · HH:mm")}
                        </td>
                        <td className="px-4 py-2 text-right font-medium tabular-nums">
                          {isEditing ? (
                            <input
                              type="number"
                              step="any"
                              min="0"
                              autoFocus
                              value={editValue}
                              disabled={savingEdit}
                              onChange={(e) => setEditValue(e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === "Enter") { e.preventDefault(); saveEdit(r); }
                                if (e.key === "Escape") { e.preventDefault(); cancelEdit(); }
                              }}
                              data-testid={`valuation-edit-input-${r.id}`}
                              className="w-32 h-8 px-2 rounded-md border border-[#1C7C54]/60 bg-white text-right text-sm focus:outline-none focus:ring-1 focus:ring-[#1C7C54]/40"
                            />
                          ) : (
                            formatCurrency(r.value, currency)
                          )}
                        </td>
                        <td className="px-2 py-2 text-right whitespace-nowrap">
                          {isEditing ? (
                            <div className="inline-flex items-center gap-1">
                              <button
                                type="button"
                                data-testid={`valuation-edit-save-${r.id}`}
                                disabled={savingEdit}
                                onClick={() => saveEdit(r)}
                                title="Guardar"
                                className="p-1.5 rounded-lg bg-[#1C7C54] text-white hover:bg-[#0F291E] disabled:opacity-40"
                              >
                                <Check className="w-3.5 h-3.5" />
                              </button>
                              <button
                                type="button"
                                data-testid={`valuation-edit-cancel-${r.id}`}
                                disabled={savingEdit}
                                onClick={cancelEdit}
                                title="Cancelar"
                                className="p-1.5 rounded-lg hover:bg-[#EBEBEA] text-[#5F7368]"
                              >
                                <X className="w-3.5 h-3.5" />
                              </button>
                            </div>
                          ) : (
                            <div className="inline-flex items-center gap-1">
                              <button
                                type="button"
                                data-testid={`valuation-edit-${r.id}`}
                                onClick={() => startEdit(r)}
                                title="Editar importe"
                                className="p-1.5 rounded-lg hover:bg-[#EBEBEA] text-[#5F7368]"
                              >
                                <Pencil className="w-3.5 h-3.5" />
                              </button>
                              <AlertDialog>
                                <AlertDialogTrigger asChild>
                                  <button
                                    type="button"
                                    data-testid={`valuation-delete-${r.id}`}
                                    disabled={deletingId === r.id}
                                    title="Eliminar esta tasación"
                                    className="p-1.5 rounded-lg hover:bg-[#D95D39]/10 text-[#D95D39] disabled:opacity-40"
                                  >
                                    <Trash2 className="w-3.5 h-3.5" />
                                  </button>
                                </AlertDialogTrigger>
                                <AlertDialogContent className="bg-white">
                                  <AlertDialogHeader>
                                    <AlertDialogTitle>¿Eliminar esta tasación?</AlertDialogTitle>
                                    <AlertDialogDescription>
                                      {formatCurrency(r.value, currency)} · {format(parseISO(r.date), "dd MMM yyyy · HH:mm")}.
                                      Si era la más reciente, el valor actual del inmueble vuelve a la tasación anterior.
                                    </AlertDialogDescription>
                                  </AlertDialogHeader>
                                  <AlertDialogFooter>
                                    <AlertDialogCancel>Cancelar</AlertDialogCancel>
                                    <AlertDialogAction
                                      data-testid={`valuation-delete-confirm-${r.id}`}
                                      onClick={() => handleDelete(r)}
                                      className="bg-[#D95D39] hover:bg-[#D95D39]/90"
                                    >
                                      Eliminar
                                    </AlertDialogAction>
                                  </AlertDialogFooter>
                                </AlertDialogContent>
                              </AlertDialog>
                            </div>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
